// Entrar a la Consola con la cuenta de Twenty y elegir la empresa.
import { useState, type FormEvent } from 'react';

import { pedirEnlaceClave } from '../api/acceso';
import { enterWithApiKey, enterWorkspace, signIn, type Empresa } from '../api/auth';
import { DOMINIO, empresaDeWorkspace, empresaDelHost } from '../lib/direcciones';
import { Icon } from '../lib/icons';
import { KEYS, readJSON, writeJSON } from '../lib/storage';
import { Avatar } from '../ui/bits';

export function Login() {
  const [email, setEmail] = useState(() => readJSON<string>(KEYS.ultimoEmail, ''));
  const [clave, setClave] = useState('');
  const [empresas, setEmpresas] = useState<Empresa[] | null>(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [modoKey, setModoKey] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [nombreKey, setNombreKey] = useState('');
  const [olvide, setOlvide] = useState(false);
  const [enlaceEnviado, setEnlaceEnviado] = useState(false);

  const pedirEnlace = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setCargando(true);
    try {
      await pedirEnlaceClave(email);
      setEnlaceEnviado(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCargando(false);
    }
  };

  const entrar = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setCargando(true);
    try {
      const lista = await signIn(email, clave);
      writeJSON(KEYS.ultimoEmail, email.trim());
      // En <empresa>.crm.xmartek.co se entra directo a esa empresa; en la entrada general se elige.
      const deEstaDireccion = empresaDelHost();
      const propia = deEstaDireccion ? lista.find((w) => empresaDeWorkspace(w.url) === deEstaDireccion) : undefined;
      if (!lista.length) setError('Tu usuario no pertenece a ninguna empresa en este servidor.');
      else if (propia) await enterWorkspace(email.trim(), propia);
      else if (deEstaDireccion) setError(`Tu usuario no tiene acceso a la empresa de esta dirección (${deEstaDireccion}). Revisa la dirección o entra por ${DOMINIO}.`);
      else if (lista.length === 1) await enterWorkspace(email.trim(), lista[0]);
      else setEmpresas(lista);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCargando(false);
    }
  };

  const elegir = async (w: Empresa) => {
    setError('');
    setCargando(true);
    try {
      await enterWorkspace(email.trim(), w);
    } catch (err) {
      setError((err as Error).message);
      setCargando(false);
    }
  };

  const entrarKey = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setCargando(true);
    try {
      await enterWithApiKey(apiKey, nombreKey.trim() || 'Empresa');
    } catch (err) {
      setError(`No se pudo entrar con esa API key: ${(err as Error).message}`);
      setCargando(false);
    }
  };

  return (
    <div className="login">
      <div className="login-card">
        <div className="brand" style={{ padding: 0 }}>
          <span className="mark">
            <Icon name="wave" />
          </span>
          <span className="bt">
            <b>CRM XmarteK</b>
            <small>CRM + 3CX</small>
          </span>
        </div>

        {olvide ? (
          <>
            <div>
              <h1>¿Olvidaste tu contraseña?</h1>
              <p className="lead">
                {enlaceEnviado
                  ? `Si ${email} tiene cuenta en esta empresa, le llegó un correo con el enlace para crear una contraseña nueva.`
                  : 'Te enviamos un enlace a tu correo para crear una nueva.'}
              </p>
            </div>
            {enlaceEnviado ? null : (
              <form className="form" onSubmit={pedirEnlace}>
                <label>
                  Correo
                  <input id="o-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" autoFocus />
                </label>
                {error ? (
                  <p className="error" role="alert">
                    {error}
                  </p>
                ) : null}
                <button className="btn primary lg" disabled={cargando || !email}>
                  {cargando ? 'Enviando…' : 'Enviar enlace'}
                </button>
              </form>
            )}
            <button className="linkbtn" onClick={() => (setOlvide(false), setEnlaceEnviado(false), setError(''))}>
              Volver a entrar
            </button>
          </>
        ) : empresas ? (
          <>
            <div>
              <h1>¿En qué empresa vas a trabajar?</h1>
              <p className="lead">{email}</p>
            </div>
            <div className="ws-list">
              {empresas.map((w) => (
                <button key={w.id} className="ws-it" onClick={() => elegir(w)} disabled={cargando}>
                  <Avatar nombre={w.nombre} square />
                  <span className="bt">
                    <b>{w.nombre}</b>
                    <small>{w.url.replace(/^https?:\/\//, '')}</small>
                  </span>
                  <Icon name="chevRight" />
                </button>
              ))}
            </div>
            {error ? <p className="note err">{error}</p> : null}
            <button className="linkbtn" onClick={() => (setEmpresas(null), setClave(''))}>
              Usar otra cuenta
            </button>
          </>
        ) : modoKey ? (
          <>
            <div>
              <h1>Entrar con API key</h1>
              <p className="lead">
                Para puestos compartidos o pruebas. La key la entrega el administrador de tu CRM. Con API key la empresa comparte un cupo de 100 consultas por minuto con la central
                3CX; para el trabajo diario entra con tu correo.
              </p>
            </div>
            <form className="form" onSubmit={entrarKey}>
              <label>
                Nombre de la empresa
                <input id="l-nombre" value={nombreKey} onChange={(e) => setNombreKey(e.target.value)} placeholder="Cliente Demo A" autoComplete="off" />
              </label>
              <label>
                API key
                <input id="l-apikey" value={apiKey} onChange={(e) => setApiKey(e.target.value)} type="password" required autoComplete="off" />
              </label>
              {error ? <p className="error">{error}</p> : null}
              <button className="btn primary lg" disabled={cargando || !apiKey.trim()}>
                {cargando ? 'Verificando…' : 'Entrar'}
              </button>
            </form>
            <button className="linkbtn" onClick={() => (setModoKey(false), setError(''))}>
              Entrar con correo y contraseña
            </button>
          </>
        ) : (
          <>
            <div>
              <h1>Hola de nuevo</h1>
              <p className="lead">Entra con tu cuenta del CRM.</p>
            </div>
            <form className="form" onSubmit={entrar}>
              <label>
                Correo
                <input id="l-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" autoFocus={!email} />
              </label>
              <label>
                Contraseña
                <input id="l-clave" type="password" value={clave} onChange={(e) => setClave(e.target.value)} required autoComplete="current-password" autoFocus={!!email} />
              </label>
              {error ? (
                <p className="error" role="alert">
                  {error}
                </p>
              ) : null}
              <button className="btn primary lg" disabled={cargando || !email || !clave}>
                {cargando ? 'Entrando…' : 'Entrar'}
              </button>
            </form>
            {empresaDelHost() ? (
              <button className="linkbtn" onClick={() => (setOlvide(true), setError(''))}>
                ¿Olvidaste tu contraseña?
              </button>
            ) : null}
            <button className="linkbtn" onClick={() => (setModoKey(true), setError(''))}>
              Entrar con API key
            </button>
          </>
        )}
      </div>
    </div>
  );
}
